import { ref } from 'vue'
import { toast } from 'vue-sonner'
import { useApi } from '../useApi.js'
import { toDatetimeLocalValue } from '../../utils/dateFormatters.js'

/**
 * Composable pour gérer les appels dans l'historique
 * Limite: 80 lignes max
 */
export function useQuoteHistoryCalls(quoteId, loadHistory) {
  const showCallForm = ref(false)
  const editingCall = ref(null)
  const savingCall = ref(false)
  const callForm = ref({
    callDate: '',
    direction: '',
    result: '',
    notes: ''
  })
  
  const { post: apiPost, put: apiPut, delete: apiDelete } = useApi()

  const editCall = (call) => {
    editingCall.value = call
    callForm.value = {
      callDate: toDatetimeLocalValue(call.call_date),
      direction: call.direction || '',
      result: call.result || '',
      notes: call.notes || ''
    }
    showCallForm.value = true
  }

  const closeCallForm = () => {
    showCallForm.value = false
    editingCall.value = null
    callForm.value = {
      callDate: '',
      direction: '',
      result: '',
      notes: ''
    }
  }

  const saveCall = async (callData = null) => {
    // Utiliser les données passées en paramètre ou celles du formulaire
    const dataToSave = callData || callForm.value
    
    if (!dataToSave.callDate || !dataToSave.direction) {
      throw new Error('Veuillez remplir tous les champs obligatoires')
    }
    
    savingCall.value = true
    try {
      const callDateISO = dataToSave.callDate 
        ? new Date(dataToSave.callDate).toISOString()
        : null
      
      const body = editingCall.value
        ? {
            callDate: callDateISO,
            direction: dataToSave.direction,
            result: dataToSave.result || null,
            notes: dataToSave.notes || null
          }
        : {
            quoteId: parseInt(quoteId),
            callDate: callDateISO,
            direction: dataToSave.direction,
            result: dataToSave.result || null,
            notes: dataToSave.notes || null,
            // Saisie manuelle : le texte est intégralement du commercial, il
            // doit remonter dans le fil de commentaires comme avant.
            commentaire: dataToSave.notes || null
          }
      
      if (editingCall.value) {
        await apiPut(`/api/calls/${editingCall.value.id}`, body, {
          showSuccessToast: false,
          errorMessage: 'Erreur lors de l\'enregistrement de l\'appel'
        })
      } else {
        await apiPost('/api/calls', body, {
          showSuccessToast: false,
          errorMessage: 'Erreur lors de l\'enregistrement de l\'appel'
        })
      }
      
      const wasEditing = !!editingCall.value
      await loadHistory()
      closeCallForm()
      toast.success(wasEditing ? 'Appel modifié avec succès' : 'Appel enregistré avec succès')
      return true
    } catch (err) {
      console.error(err)
      toast.error('Erreur lors de l\'enregistrement de l\'appel')
      throw err
    } finally {
      savingCall.value = false
    }
  }

  const deleteCall = async (callId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet appel ?')) {
      return false
    }
    
    try {
      await apiDelete(`/api/calls/${callId}`, {
        showSuccessToast: false,
        errorMessage: 'Erreur lors de la suppression de l\'appel'
      })
      await loadHistory()
      return true
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  return {
    showCallForm,
    editingCall,
    savingCall,
    callForm,
    editCall,
    closeCallForm,
    saveCall,
    deleteCall
  }
}
